<script>
  import { getContext, tick } from "svelte";
  import Block from "#lib/Block.svelte";
  import BlockGrid from "#lib/BlockGrid.svelte";
  import ButtonLabel from "#lib/ButtonLabel.svelte";
  import { is_cursor_at_start, set_cursor_at_end } from "#lib/util.js";
  import t from "#lib/i18n.svelte.js";
  import { get_site_default_track } from "#lib/StoryLayouts.js";

  let { 
    container,
    grid_size,
    class: css_class,
  } = $props();
  let entry_api = getContext("entry_api");
  let site_settings = getContext("site_settings");

  // Holds the ref to the Containers' root DOM element, which is owned by <BlockGrid>
  let container_ref = $state();

  // Hand the container the site's default width so new blocks can adopt it. The setting
  // lives on the dynamic island (menu_track_start/end), so blocks line up with the menu
  // whatever width it is set to - this is what the "we need a default layout that works
  // for all of them" note in ContainerModel.add_block was missing.
  $effect(() => {
    if (container) container.site_default_track = get_site_default_track(site_settings);
  });

  export function focus_block(block_index, target = 'start') {
    // Will try to set the text cursor for a given block_index (if contenteditable fields are found)
    const fields = [...container_ref.querySelectorAll(`.block:nth-child(${block_index + 1}) [tabindex="0"]`)];
    if (target === 'start') {
      fields[0]?.focus();
    } else {
      set_cursor_at_end(fields.pop());
    }
  }

  export async function scroll_block_into_view(block_index) {
    if (!(block_index >= 0)) {
      console.warn('No block_index provided to Container.scroll_block_into_view()', block_index);
      return;
    }
    
    // Wait for DOM to update after block reorder
    await tick();
    
    const block_el = container_ref.querySelector(`.block:nth-child(${block_index + 1})`);
    if (!block_el) return;
    
    // Align block's top edge with vertical center of viewport
    // This keeps up/down move buttons in same position for repeated clicks
    const block_rect = block_el.getBoundingClientRect();
    const viewport_center_y = window.innerHeight * 0.3;
    const scroll_target = window.scrollY + block_rect.top - viewport_center_y;
    
    window.scrollTo({
      top: scroll_target,
      behavior: 'instant'
    });
  }
  
  // These methods are called by keyboard commands (see DynamicIsland.svelte)
  // --------------------------------------------------------------------------

  export function is_at_start_of_first_field(block_index, focused_element) {
    const first_focusable_field = container_ref.querySelector(`.block:nth-child(${block_index + 1}) [tabindex="0"]`);
    return focused_element === first_focusable_field && is_cursor_at_start(focused_element);
  }


  export function is_inside_container(el) {
    return container_ref.contains(el);
  }

  // For a textish block we assume it is empty when all visible fields are empty.
  export function is_block_empty(block_index) {
    const fields = container_ref.querySelectorAll(`.block:nth-child(${block_index + 1}) [tabindex="0"]`);
    return Array.from(fields).every(field => field.textContent.trim() === '');
  }

  function handle_selection_change(e) {
    if (!entry_api.is_editing) return;

    function get_block_index(block_el) {
      return Array.from(block_el.parentElement.children).indexOf(block_el);
    }

    const field_el = e.currentTarget.activeElement;

    // only handle selection changes inside the container at hand
    if (!container_ref?.contains(field_el)) return;
    const block_el = field_el.closest('.block');

    if (block_el && field_el.getAttribute('contenteditable') === 'true') {
      // We now know the selection is inside an editable <Text> that's part of a block
      const block_index = get_block_index(block_el);

      entry_api.active_container = container;

      if (field_el.dataset.focusBlock) {        
        // We've hit a focus trap of a non-text block (like Embed), so we select
        // the full block
        container.set_selection({
          type: 'container',
          block_indexes: [block_index],
          block_anchor_index: block_index
        });
        field_el.blur(); // remove focus from the focus trap
        field_el.scrollIntoView();
      } else {

        const field_name = field_el.closest('[data-field-name]')?.dataset.fieldName;

        container.set_selection({
          type: 'field',
          block_index,
          field_name,
        });
      }
    } else {
      // do nothing?
      // OR: clear selection in certain cases?
    }
  }

  function select_block(index, field_name) {
    if (entry_api.is_editing) {
      entry_api.active_container = container;

      if (field_name) {
        container.set_selection({
          type: 'field',
          block_index: index,
          field_name
        });
      } else {
        container.set_selection({
          type: 'container',
          block_indexes: [index],
          block_anchor_index: index,
        });
      }
    }
  }
</script>

<BlockGrid class='container {css_class}' bind:ref={container_ref} {grid_size}>
  {#each container.blocks as block, i (block.block_id)}
    <Block
      onpointerdown={(e)=> {
        if (!entry_api.is_editing) return;
        // Prevents race condition block should not be selected another time when clicking on a contenteditable field
        if (e.target.getAttribute('contenteditable') === 'true') { return };
        
        if (e.metaKey || e.ctrlKey) {
          container.toggle_block_selection(i);
        } else if (e.shiftKey) {
          // Handle SHIFT + click for range selection
          if (container.selection?.type === 'container' && container.selection.block_anchor_index >= 0) {
            const anchor_index = container.selection.block_anchor_index;
            const start = Math.min(anchor_index, i);
            const end = Math.max(anchor_index, i);
            const new_block_indexes = Array.from({ length: end - start + 1 }, (_, k) => start + k);
            container.set_selection({
              type: 'container',
              block_indexes: new_block_indexes,
              block_anchor_index: anchor_index, // Keep the original anchor
            });
          } else {
            // If no valid anchor, treat as a normal click (sets the new anchor)
            select_block(i);
          }
        } else {
          // NOTE: We disable field selections on click for now
          select_block(i /*, e.target.dataset.fieldName*/);
        }
      }}
      ondblclick={(e) => {
        // allow text selection on double click when focus is text
        if (
          e.target.getAttribute('contenteditable') === 'true'
          || /text/.test(e.target.className)
        ) {
          return;
        };
        // ... But prevent selection of texts & images when focus is on the block background
        if (entry_api.is_editing) {
          e.preventDefault();
          e.stopPropagation();
          if (window.getSelection) {
            window.getSelection().removeAllRanges();
          } else if (document.selection) {
            document.selection.empty();
          }
        };
      }}
      {block}
      {container}
      block_index={i}
      total_blocks={container.blocks.length}
      selected={container.is_index_selected(i)}
      is_block_selection={container.is_container_selection()}
      is_editing={entry_api.is_editing}
    />
  {/each}
</BlockGrid>

<!-- BLOCK TOOL BAR -->
{#if entry_api.is_editing}
  <div class="add-blocks border-md my m-6 flex-row justify-center ">
    <button
      onclick={()=>{ container.add_block('story', { description: ''}, { position: -1})}}
      style="--button-outer-spacing: var(--size-4)"
    >
      <ButtonLabel align='center' variant='brand subtle-outlined' start_icon='plus'>{t('add_block')}</ButtonLabel>
    </button>
  </div>
{/if}

<svelte:document onselectionchange={handle_selection_change} />

<style>
  .add-blocks {
    @media (max-width: 1024px) {
      display: none;
    }
  }
</style>